import React, { useEffect } from "react";
import PageContainer from "../../components/PageContainer/index";
import LoadBoundary from "../../components/LoadBoundary";
import { Switch } from "react-router-dom";
import ProtectedOrganisationRoute from "../../components/ProtectedOrganisationRoute";
import LoadingMessage from "../../components/LoadingMessage";
import { useDispatch } from "react-redux";
import useAddon from "../../hooks/useAddon";
import useUser from "../../hooks/useUser";
import usePush from "../../hooks/usePush";
import AccountService from "../../services/account-service";
import { setUser } from "../login/duck/actions/login";
import useShortCode from "../../hooks/useShortCode";
import ReceivablesForecastTease from "./components/ReceivablesForecastTease.jsx";
import { addonIds } from "../../util/constants";
import RemoteUnavailable from "../../components/RemoteUnavailable";
import useModuleInfo from "../../hooks/useModuleInfo";

const ReceivablesForecastUnavailable = () => (
  <RemoteUnavailable errorCode="RF-503" />
);

const CashflowAppLazy = React.lazy(() =>
  import("cashflow/App").catch((error) => ({
    default: ReceivablesForecastUnavailable,
  })),
);

const CashflowRemoteRoute = () => {
  const sc = useShortCode();

  return <CashflowAppLazy key={sc || "no-org"} organisation={sc} />;
};

const CashflowGate = () => {
  const dispatch = useDispatch();
  const { isSubscribed, loading: addonLoading } = useAddon(
    addonIds.RECEIVABLES_FORECAST,
  );
  const { features, token, userId } = useUser();
  const { execute: executeGetCurrentUser } = usePush(
    AccountService.getCurrentUser,
    {
      suspense: false,
    },
  );
  const { module } = useModuleInfo(undefined, { suspense: false });
  const { subscriber, isInTrial: isChaserTrial } = module || {};
  const isSubscriber = userId && subscriber?._id === userId;

  useEffect(() => {
    const updateUser = async () => {
      const [err, updatedUser] = await executeGetCurrentUser();
      if (!err && updatedUser) {
        dispatch(setUser({ ...updatedUser, token }));
      }
    };

    updateUser();
  }, [dispatch, executeGetCurrentUser, token]);

  if (addonLoading) return <LoadingMessage center />;

  if (isSubscribed) return <CashflowRemoteRoute />;

  const trialEndDate = features?.receivablesForecastTrialEndDate;
  const isTrialActive = trialEndDate && new Date(trialEndDate) > new Date();
  const isTrialExpired = trialEndDate && !isTrialActive;

  if (isTrialActive) return <CashflowRemoteRoute />;

  return (
    <ReceivablesForecastTease
      isTrialExpired={isTrialExpired}
      isSubscriber={isSubscriber}
      isChaserTrial={isChaserTrial}
    />
  );
};

const Cashflow = () => {
  return (
    <PageContainer maxWidth="initial">
      <LoadBoundary>
        <Switch>
          <ProtectedOrganisationRoute
            path={["/cashflow/receivables", "/cashflow/receivables/*"]}
            component={CashflowGate}
          />
        </Switch>
      </LoadBoundary>
    </PageContainer>
  );
};

export default Cashflow;
